import React from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Stack,
  CircularProgress,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  Divider,
} from '@mui/material';
import {
  BusinessOutlined as BusinessIcon,
  PersonOutlined as PersonIcon,
} from '@mui/icons-material';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { usePermissions } from '@billda/permissions-ui';
import { RHFNinoInput, getErrorMessage } from '@billda/shared-ui';
import {
  businessDetailsCaptureSchema,
  BusinessDetailsCaptureFormData,
  defaultBusinessDetailsValues,
  normalizeUtr,
  normalizeCrn,
} from '../types/businessDetailsCaptureSchema';
import { useBusinessDetailsCapture } from '../hooks/useBusinessDetailsCapture';
import { UpsertBusinessDetailsRequest } from '../api/businessDetailsApi';

interface BusinessDetailsCaptureFormProps {
  organisationName: string;
  onSuccess: () => void;
  onError: (message: string) => void;
}

const BusinessDetailsCaptureForm: React.FC<BusinessDetailsCaptureFormProps> = ({
  organisationName,
  onSuccess,
  onError,
}) => {
  const permissions = usePermissions();
  const canSkip = permissions.includes('users.invite');

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = useForm<BusinessDetailsCaptureFormData>({
    defaultValues: defaultBusinessDetailsValues,
    resolver: yupResolver(businessDetailsCaptureSchema) as any,
    mode: 'onChange',
  });

  const businessType = watch('businessType');

  const { mutate: saveBusinessDetails, isPending } =
    useBusinessDetailsCapture();

  const mutateCallbacks = {
    onSuccess: () => {
      onSuccess();
    },
    onError: (error: Error) => {
      onError(getErrorMessage(error, 'Failed to save business details'));
    },
  };

  const handleSkip = () => {
    // When skipping, save with isBookingWork = false
    saveBusinessDetails(
      {
        isBookingWork: false,
      },
      mutateCallbacks
    );
  };

  const onSubmit = (data: BusinessDetailsCaptureFormData) => {
    const request: UpsertBusinessDetailsRequest = {
      isBookingWork: true,
      businessName: data.businessName || undefined,
    };

    if (data.businessType === 'soleTrader') {
      request.soleTrader = {
        hmrcFullName: data.soleTrader.hmrcFullName,
        utr: data.soleTrader.utr,
        nationalInsuranceNumber:
          data.soleTrader.nationalInsuranceNumber.toUpperCase(),
      };
    } else {
      request.limitedCompany = {
        companyName: data.limitedCompany.companyName,
        utr: data.limitedCompany.utr,
        companyRegistrationNumber:
          data.limitedCompany.companyRegistrationNumber.toUpperCase(),
      };
    }

    saveBusinessDetails(request, mutateCallbacks);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box textAlign="center" mb={4}>
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          data-testid="business-details-title"
          sx={{ color: 'primary.main', fontWeight: 600 }}
        >
          Business Details
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Provide your business information to complete your profile for{' '}
          {organisationName}
        </Typography>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={3}>
          {/* Business Type Selection */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Business Type
            </Typography>
            <Controller
              name="businessType"
              control={control}
              render={({ field }) => (
                <ToggleButtonGroup
                  {...field}
                  exclusive
                  fullWidth
                  color="primary"
                  onChange={(_, value) => {
                    if (value) field.onChange(value);
                  }}
                  data-testid="business-type-toggle"
                >
                  <ToggleButton
                    value="soleTrader"
                    data-testid="sole-trader-btn"
                  >
                    <PersonIcon sx={{ mr: 1 }} />
                    Sole Trader
                  </ToggleButton>
                  <ToggleButton
                    value="limitedCompany"
                    data-testid="limited-company-btn"
                  >
                    <BusinessIcon sx={{ mr: 1 }} />
                    Limited Company
                  </ToggleButton>
                </ToggleButtonGroup>
              )}
            />
            {errors.businessType && (
              <Typography color="error" variant="caption">
                {errors.businessType.message}
              </Typography>
            )}
          </Box>

          {/* Business Name (optional) */}
          <Controller
            name="businessName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Trading Name (Optional)"
                fullWidth
                error={!!errors.businessName}
                helperText={errors.businessName?.message}
                disabled={isPending}
                data-testid="business-name-input"
              />
            )}
          />

          <Divider />

          {/* Sole Trader Fields */}
          {businessType === 'soleTrader' && (
            <>
              <Typography variant="subtitle1" fontWeight="medium">
                CIS Details - Sole Trader
              </Typography>

              <Controller
                name="soleTrader.hmrcFullName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Full Name (as registered with HMRC)"
                    fullWidth
                    error={!!errors.soleTrader?.hmrcFullName}
                    helperText={errors.soleTrader?.hmrcFullName?.message}
                    disabled={isPending}
                    data-testid="hmrc-full-name-input"
                  />
                )}
              />

              <Controller
                name="soleTrader.utr"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    onChange={e => field.onChange(normalizeUtr(e.target.value))}
                    label="Unique Taxpayer Reference (UTR)"
                    fullWidth
                    error={!!errors.soleTrader?.utr}
                    helperText={
                      errors.soleTrader?.utr?.message ||
                      '10 digits - found on your Self Assessment tax return (SA250)'
                    }
                    disabled={isPending}
                    inputProps={{ maxLength: 11 }}
                    data-testid="sole-trader-utr-input"
                  />
                )}
              />

              <RHFNinoInput
                name="soleTrader.nationalInsuranceNumber"
                control={control}
                label="National Insurance Number"
                fullWidth
                helperText="Format: AB 12 34 56 C - found on your payslip, P60, or NI card"
                disabled={isPending}
                data-testid="nino-input"
              />
            </>
          )}

          {/* Limited Company Fields */}
          {businessType === 'limitedCompany' && (
            <>
              <Typography variant="subtitle1" fontWeight="medium">
                CIS Details - Limited Company
              </Typography>

              <Controller
                name="limitedCompany.companyName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Company Name (as registered)"
                    fullWidth
                    error={!!errors.limitedCompany?.companyName}
                    helperText={errors.limitedCompany?.companyName?.message}
                    disabled={isPending}
                    data-testid="company-name-input"
                  />
                )}
              />

              <Controller
                name="limitedCompany.utr"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    onChange={e => field.onChange(normalizeUtr(e.target.value))}
                    label="Company UTR"
                    fullWidth
                    error={!!errors.limitedCompany?.utr}
                    helperText={
                      errors.limitedCompany?.utr?.message ||
                      '10 digits - found on your Corporation Tax letter (CT41G)'
                    }
                    disabled={isPending}
                    inputProps={{ maxLength: 11 }}
                    data-testid="company-utr-input"
                  />
                )}
              />

              <Controller
                name="limitedCompany.companyRegistrationNumber"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    onChange={e => field.onChange(normalizeCrn(e.target.value))}
                    label="Company Registration Number (CRN)"
                    fullWidth
                    error={!!errors.limitedCompany?.companyRegistrationNumber}
                    helperText={
                      errors.limitedCompany?.companyRegistrationNumber
                        ?.message ||
                      '8 characters - found on your certificate of incorporation'
                    }
                    disabled={isPending}
                    inputProps={{
                      maxLength: 8,
                      style: { textTransform: 'uppercase' },
                    }}
                    data-testid="crn-input"
                  />
                )}
              />
            </>
          )}

          <Alert severity="info" sx={{ mt: 2 }}>
            This information is required for CIS (Construction Industry Scheme)
            compliance and will be verified by the organisation.
          </Alert>

          <Stack
            justifyContent="space-between"
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
          >
            {canSkip ? (
              <Button
                variant="outlined"
                onClick={handleSkip}
                disabled={isPending}
                data-testid="skip-business-details-button"
              >
                I am not booking in work - Skip
              </Button>
            ) : (
              <Button
                variant="outlined"
                onClick={onSuccess}
                disabled={isPending}
                data-testid="skip-for-now-button"
              >
                Skip for now
              </Button>
            )}
            <Box sx={{ flexGrow: 1 }} />
            <Button
              type="submit"
              variant="contained"
              disabled={!isValid || isPending}
              data-testid="submit-business-details-button"
            >
              {isPending ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Saving...
                </>
              ) : (
                'Save and Continue'
              )}
            </Button>
          </Stack>
        </Stack>
      </form>
    </Box>
  );
};

export default BusinessDetailsCaptureForm;
